import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import GestorLayout from '../../components/GestorLayout';
import GestaoTags, { OperadorTags, TagSelector } from '../../components/GestaoTags';
import OperadorPermissoes from '../../components/OperadorPermissoes';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Plus, User, Edit2, Grid, List, Star, Eye, EyeOff, Camera, Save, Trash2, Upload, X, Tag, Settings, FlaskConical, Clock }from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cores padrão para categorias (caso não tenham cor definida)
const CATEGORIA_COLORS = [
  'bg-blue-100 text-blue-700 border-blue-200',
  'bg-amber-100 text-amber-700 border-amber-200',
  'bg-green-100 text-green-700 border-green-200',
  'bg-purple-100 text-purple-700 border-purple-200',
  'bg-red-100 text-red-700 border-red-200',
  'bg-teal-100 text-teal-700 border-teal-200'
];

const AVATARS = [
  '👤', '👩', '👨', '👩‍🦰', '👨‍🦰', '👩‍🦱', '👨‍🦱', '👩‍🦳', '👨‍🦳', '👧', '👦', '🧑', '👴', '👵'
];

export default function GestaoOperadores() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [operadores, setOperadores] = useState([]);
  const [desempenhos, setDesempenhos] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [editingOperador, setEditingOperador] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' ou 'list'
  const [showRating, setShowRating] = useState(true);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [showTagsDialog, setShowTagsDialog] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [filterTag, setFilterTag] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [operacoes, setOperacoes] = useState([]);
  const [showPermissoes, setShowPermissoes] = useState(null); // operador selecionado para permissões
  const [modoTestes, setModoTestes] = useState(false); // Modo testes - esconder tempos
  
  // Verificar se há um ID na URL para abrir directamente
  useEffect(() => {
    const operadorId = searchParams.get('id');
    if (operadorId && operadores.length > 0) {
      const operador = operadores.find(op => op.id === operadorId);
      if (operador) {
        handleEdit(operador);
        // Limpar o parâmetro da URL
        setSearchParams({});
      }
    }
  }, [searchParams, operadores, setSearchParams]);
  
  const [formData, setFormData] = useState({
    username: '',
    nome: '',
    apelido: '',
    data_nascimento: '',
    categorias: [],
    custo_hora: 10,
    avatar: '👤',
    foto_url: '',
    tags: []
  });

  useEffect(() => {
    loadOperadores();
    loadDesempenhos();
    loadTags();
    loadCategorias();
    loadOperacoes();
    loadConfiguracao();
  }, []);

  const loadOperacoes = async () => {
    try {
      const response = await axios.get(`${API}/operacoes`);
      setOperacoes(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar operações:', error);
    }
  };

  const loadCategorias = async () => {
    try {
      const response = await axios.get(`${API}/categorias`);
      // Mapear categorias da BD para o formato esperado
      const cats = response.data.map((cat, index) => ({
        value: cat.id,
        label: cat.nome,
        color: CATEGORIA_COLORS[index % CATEGORIA_COLORS.length]
      }));
      setCategorias(cats);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const loadConfiguracao = async () => {
    try {
      const response = await axios.get(`${API}/configuracao`);
      setModoTestes(response.data.modo_testes_esconder_tempos || false);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
    }
  };

  const toggleModoTestes = async () => {
    try {
      // Carregar config atual
      const configRes = await axios.get(`${API}/configuracao`);
      const config = configRes.data;
      
      // Inverter o modo
      const novoModo = !modoTestes;
      
      // Atualizar no backend
      await axios.put(`${API}/configuracao`, {
        ...config,
        modo_testes_esconder_tempos: novoModo
      });
      
      setModoTestes(novoModo);
      toast.success(novoModo 
        ? 'Modo Testes ativado - Tempos e eficiências ocultos para operadores' 
        : 'Modo Testes desativado - Tempos e eficiências visíveis'
      );
    } catch (error) {
      toast.error('Erro ao alterar modo de testes');
      console.error('Erro:', error);
    }
  };

  const loadTags = async () => {
    try {
      const response = await axios.get(`${API}/tags`);
      setAllTags(response.data);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    }
  };

  const loadOperadores = async () => {
    try {
      const response = await axios.get(`${API}/operadores`);
      setOperadores(response.data.filter(op => op.ativo !== false));
    } catch (error) {
      console.error('Erro ao carregar operadores:', error);
    }
  };

  const loadDesempenhos = async () => {
    try {
      const response = await axios.get(`${API}/analytics/operadores/desempenho?periodo=mes`);
      const map = {};
      response.data.forEach(d => {
        map[d.id] = d;
      });
      setDesempenhos(map);
    } catch (error) {
      console.error('Erro ao carregar desempenhos:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      nome: '',
      apelido: '',
      data_nascimento: '',
      categorias: [],
      custo_hora: 10,
      avatar: '👤',
      foto_url: '',
      tags: []
    });
    setEditingOperador(null);
  };

  const handleEdit = (operador) => {
    // Separar nome e apelido
    const partes = (operador.nome_completo || '').split(' ');
    const nome = partes[0] || '';
    const apelido = partes.slice(1).join(' ') || '';
    
    // Converter categoria única para array
    let categorias = [];
    if (operador.categorias && Array.isArray(operador.categorias)) {
      categorias = operador.categorias;
    } else if (operador.categoria) {
      categorias = [operador.categoria];
    }
    
    setFormData({
      username: operador.username || '',
      nome: nome,
      apelido: apelido,
      data_nascimento: operador.data_nascimento || '',
      categorias: categorias,
      custo_hora: operador.custo_hora || 10,
      avatar: operador.avatar || '👤',
      foto_url: operador.foto_url || '',
      tags: operador.tags || []
    });
    setEditingOperador(operador);
    setShowDialog(true);
  };

  // Função para fazer upload da foto
  const handleFotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validar tamanho (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ficheiro muito grande. Máximo 5MB.');
      return;
    }
    
    // Se estiver a editar, fazer upload imediatamente
    if (editingOperador) {
      setUploadingFoto(true);
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(
          `${API}/operadores/${editingOperador.id}/foto`,
          formDataUpload,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        setFormData({ ...formData, foto_url: response.data.foto_url });
        toast.success('Foto carregada com sucesso!');
      } catch (error) {
        toast.error('Erro ao carregar foto');
        console.error(error);
      } finally {
        setUploadingFoto(false);
      }
    } else {
      // Se for novo operador, guardar preview local
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, foto_url: reader.result, _fotoFile: file });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nome || !formData.apelido) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    // Mapear nomes de categorias para valores do enum
    const categoriaMap = {
      'Preparação': 'preparacao',
      'Intermédios': 'intermedios',
      'Acabamentos': 'acabamentos',
      'Embalamento': 'embalamento'
    };

    setLoading(true);
    try {
      // Buscar nomes das categorias selecionadas a partir dos IDs
      const categoriasNomes = formData.categorias.map(catId => {
        const cat = categorias.find(c => c.value === catId);
        return cat?.label || catId;
      });
      
      // Mapear nomes das categorias para o formato do enum
      const categoriasEnum = categoriasNomes.map(nome => {
        return categoriaMap[nome] || nome.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, '');
      });
      
      const payload = {
        username: formData.username || `${formData.nome.toLowerCase()}.${formData.apelido.toLowerCase()}`.replace(/\s+/g, ''),
        nome_completo: `${formData.nome} ${formData.apelido}`,
        data_nascimento: formData.data_nascimento || null,
        categoria: categoriasEnum[0] || null,
        categorias: categoriasEnum,
        custo_hora: parseFloat(formData.custo_hora) || 10.0,
        avatar: formData.avatar,
        tags: formData.tags || []
      };
      
      console.log('Payload enviado:', payload);

      if (editingOperador) {
        await axios.put(`${API}/operadores/${editingOperador.id}`, payload);
        toast.success('Operador atualizado com sucesso!');
      } else {
        await axios.post(`${API}/operadores`, payload);
        toast.success('Operador criado com sucesso!');
      }

      setShowDialog(false);
      resetForm();
      loadOperadores();
    } catch (error) {
      console.error('Erro ao guardar operador:', error.response?.data);
      if (error.response?.status === 400) {
        toast.error(getErrorMessage(error, 'Username já existe'));
      } else if (error.response?.status === 422) {
        toast.error('Dados inválidos. Verifique os campos obrigatórios.');
      } else {
        toast.error('Erro ao guardar operador');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOperador = async () => {
    if (!editingOperador) return;
    
    if (!window.confirm(`Tem a certeza que deseja eliminar o operador "${editingOperador.nome_completo}"? Esta ação não pode ser revertida.`)) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`${API}/operadores/${editingOperador.id}`);
      toast.success('Operador eliminado com sucesso!');
      setShowDialog(false);
      resetForm();
      loadOperadores();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erro ao eliminar operador');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleCategoria = (cat) => {
    if (formData.categorias.includes(cat)) {
      setFormData({ ...formData, categorias: formData.categorias.filter(c => c !== cat) });
    } else {
      setFormData({ ...formData, categorias: [...formData.categorias, cat] });
    }
  };

  const getCategoriaInfo = (categoria) => {
    return categorias.find(c => c.value === categoria) || { label: categoria, color: 'bg-stone-100 text-stone-600' };
  };

  const getDesempenhoInfo = (operadorId) => {
    const d = desempenhos[operadorId];
    if (!d || d.total_registos === 0) return { cor: 'gray', estrelas: 0, eficiencia: 0 };
    
    const ef = d.eficiencia_media;
    let cor = 'gray';
    let estrelas = 0;
    
    if (ef >= 101) { cor = 'blue'; estrelas = 5; }
    else if (ef >= 95) { cor = 'green'; estrelas = 4; }
    else if (ef >= 90) { cor = 'green'; estrelas = 3; }
    else if (ef >= 80) { cor = 'yellow'; estrelas = 2; }
    else if (ef >= 70) { cor = 'orange'; estrelas = 1; }
    else { cor = 'red'; estrelas = 0; }
    
    return { cor, estrelas, eficiencia: ef };
  };

  const getCorClass = (cor) => {
    const classes = {
      'blue': 'text-blue-500',
      'green': 'text-green-500',
      'yellow': 'text-yellow-500',
      'orange': 'text-orange-500',
      'red': 'text-red-500',
      'gray': 'text-gray-300'
    };
    return classes[cor] || 'text-gray-300';
  };

  const renderEstrelas = (estrelas, cor) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star
            key={i}
            className={`h-3 w-3 ${i <= estrelas ? getCorClass(cor) : 'text-gray-200'}`}
            fill={i <= estrelas ? 'currentColor' : 'none'}
          />
        ))}
      </div>
    );
  };

  return (
    <GestorLayout>
      <div className="px-8 pt-4 pb-8">
        {/* Cabeçalho */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900 mb-2">Operadores</h1>
            <p className="text-stone-600">Gerir operadores do chão de fábrica ({operadores.length})</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Toggle Modo Testes - Esconder Tempos */}
            <Button
              variant={modoTestes ? "default" : "outline"}
              size="sm"
              onClick={toggleModoTestes}
              className={`rounded-full ${modoTestes ? 'bg-amber-500 hover:bg-amber-600 text-white' : ''}`}
              title={modoTestes ? 'Modo Testes ATIVO - Tempos ocultos para operadores' : 'Ativar Modo Testes - Esconde tempos e eficiências'}
              data-testid="modo-testes-toggle"
            >
              <FlaskConical className="h-4 w-4 mr-1" />
              {modoTestes ? 'Modo Testes ON' : 'Modo Testes'}
            </Button>
            
            {/* Toggle mostrar classificação */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRating(!showRating)}
              className={`rounded-full ${showRating ? '' : 'opacity-50'}`}
              title={showRating ? 'Ocultar classificação' : 'Mostrar classificação'}
            >
              {showRating ? <Eye className="h-4 w-4 mr-1" /> : <EyeOff className="h-4 w-4 mr-1" />}
              Rating
            </Button>
            
            {/* Toggle vista */}
            <div className="flex border rounded-full overflow-hidden">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="rounded-none"
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-none"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            {/* Botão Gerir Tags */}
            <Button
              variant="outline"
              onClick={() => setShowTagsDialog(true)}
              className="rounded-full"
              data-testid="gerir-tags-button"
            >
              <Tag className="h-4 w-4 mr-2" />
              Tags
            </Button>
            
            <Button
              onClick={() => { resetForm(); setShowDialog(true); }}
              className="rounded-full"
              data-testid="criar-operador-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Operador
            </Button>
          </div>
        </div>

        {/* Vista Grid */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {operadores.map((operador) => {
              const desemp = getDesempenhoInfo(operador.id);
              
              return (
                <Card key={operador.id} className="p-5 hover:shadow-lg transition-shadow" data-testid={`operador-card-${operador.username}`}>
                  <div className="flex items-start gap-4">
                    {/* Avatar ou Foto */}
                    <div className="relative">
                      {operador.foto_url ? (
                        <img
                          src={`${BACKEND_URL}${operador.foto_url}`}
                          alt={operador.nome_completo}
                          className="w-14 h-14 rounded-full object-cover border-2 border-primary/20"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-3xl border-2 border-primary/20">
                          {operador.avatar || '👤'}
                        </div>
                      )}
                      {showRating && desemp.estrelas > 0 && (
                        <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                          desemp.cor === 'blue' ? 'bg-blue-500' :
                          desemp.cor === 'green' ? 'bg-green-500' :
                          desemp.cor === 'yellow' ? 'bg-yellow-500' :
                          desemp.cor === 'orange' ? 'bg-orange-500' :
                          'bg-red-500'
                        }`}>
                          {desemp.estrelas}
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-heading font-bold text-stone-900 truncate">
                        {operador.nome_completo}
                      </h3>
                      <p className="text-xs text-stone-400 font-mono">@{operador.username}</p>
                      
                      {/* Classificação */}
                      {showRating && (
                        <div className="mt-1">
                          {renderEstrelas(desemp.estrelas, desemp.cor)}
                        </div>
                      )}
                    </div>
                    
                    {/* Botão Permissões */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowPermissoes(operador)}
                      className="h-8 w-8"
                      title="Gerir permissões de operações"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                    
                    {/* Botão Editar */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(operador)}
                      className="h-8 w-8"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Tags */}
                  {operador.tags && operador.tags.length > 0 && (
                    <div className="mt-4">
                      <OperadorTags tags={operador.tags} allTags={allTags} size="xs" />
                    </div>
                  )}

                  {/* Permissões de Categorias e Operações */}
                  {operador.permissoes_categorias && Object.keys(operador.permissoes_categorias).length > 0 && categorias.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-stone-100">
                      <p className="text-xs text-stone-500 mb-2 flex items-center gap-1">
                        <Settings className="h-3 w-3" /> Permissões:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(operador.permissoes_categorias).slice(0, 3).map(([catId, opsIds]) => {
                          const catInfo = categorias.find(c => c.value === catId);
                          const numOps = Array.isArray(opsIds) ? opsIds.length : 0;
                          return (
                            <span 
                              key={catId}
                              className={`text-xs px-2 py-0.5 rounded-full border ${catInfo?.color || 'bg-stone-100 text-stone-600 border-stone-200'}`}
                            >
                              {catInfo?.label || 'Categoria'} ({numOps})
                            </span>
                          );
                        })}
                        {Object.keys(operador.permissoes_categorias).length > 3 && (
                          <span className="text-xs text-stone-400">
                            +{Object.keys(operador.permissoes_categorias).length - 3} mais
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-stone-400 mt-1">
                        Total: {Object.values(operador.permissoes_categorias).reduce((acc, ops) => acc + (Array.isArray(ops) ? ops.length : 0), 0)} operações
                      </p>
                    </div>
                  )}

                  {/* Info */}
                  <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between">
                    <span className="text-sm font-bold text-primary">€{(operador.custo_hora || 0).toFixed(2)}/h</span>
                    {operador.data_nascimento && (
                      <span className="text-xs text-stone-400">
                        {new Date(operador.data_nascimento).toLocaleDateString('pt-PT')}
                      </span>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Vista Lista */}
        {viewMode === 'list' && (
          <Card className="overflow-hidden">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Operador</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Username</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-stone-600">Nascimento</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-stone-600">€/Hora</th>
                  {showRating && <th className="text-center py-3 px-4 text-sm font-semibold text-stone-600">Rating</th>}
                  <th className="text-center py-3 px-4 text-sm font-semibold text-stone-600">Ações</th>
                </tr>
              </thead>
              <tbody>
                {operadores.map((operador) => {
                  const desemp = getDesempenhoInfo(operador.id);
                  
                  return (
                    <tr key={operador.id} className="border-t border-stone-100 hover:bg-stone-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            {operador.foto_url ? (
                              <img
                                src={`${BACKEND_URL}${operador.foto_url}`}
                                alt={operador.nome_completo}
                                className="w-10 h-10 rounded-full object-cover border border-primary/20"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-xl border border-primary/20">
                                {operador.avatar || '👤'}
                              </div>
                            )}
                            {showRating && desemp.estrelas > 0 && (
                              <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
                                desemp.cor === 'blue' ? 'bg-blue-500' :
                                desemp.cor === 'green' ? 'bg-green-500' :
                                desemp.cor === 'yellow' ? 'bg-yellow-500' :
                                desemp.cor === 'orange' ? 'bg-orange-500' :
                                'bg-red-500'
                              }`}>
                                {desemp.estrelas}
                              </div>
                            )}
                          </div>
                          <span className="font-semibold text-stone-900">{operador.nome_completo}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 font-mono text-sm text-stone-500">@{operador.username}</td>
                      <td className="py-3 px-4 text-sm text-stone-500">
                        {operador.data_nascimento ? new Date(operador.data_nascimento).toLocaleDateString('pt-PT') : '-'}
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-primary">€{(operador.custo_hora || 0).toFixed(2)}</td>
                      {showRating && (
                        <td className="py-3 px-4">
                          <div className="flex justify-center">
                            {renderEstrelas(desemp.estrelas, desemp.cor)}
                          </div>
                        </td>
                      )}
                      <td className="py-3 px-4 text-center">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(operador)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}

        {operadores.length === 0 && (
          <Card className="p-12 text-center">
            <User className="h-16 w-16 mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500">Nenhum operador cadastrado</p>
          </Card>
        )}
      </div>

      {/* Dialog Criar/Editar Operador */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-lg" data-testid="criar-operador-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingOperador ? 'Editar Operador' : 'Novo Operador'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Foto e Avatar */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                {formData.foto_url ? (
                  <div className="relative">
                    <img
                      src={formData.foto_url.startsWith('data:') ? formData.foto_url : `${BACKEND_URL}${formData.foto_url}`}
                      alt="Foto do operador"
                      className="w-24 h-24 rounded-full object-cover border-2 border-primary/30"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, foto_url: '' })}
                      className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                    className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-5xl border-2 border-primary/30 hover:border-primary transition-colors"
                  >
                    {formData.avatar}
                  </button>
                )}
              </div>
              
              {/* Botão de upload de foto */}
              <div className="flex items-center gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleFotoUpload}
                    className="hidden"
                    disabled={uploadingFoto}
                  />
                  <span className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                    uploadingFoto 
                      ? 'bg-stone-100 text-stone-400 cursor-not-allowed' 
                      : 'bg-white hover:bg-primary/5 text-primary border-primary/30 hover:border-primary'
                  }`}>
                    {uploadingFoto ? (
                      <>
                        <div className="animate-spin h-3 w-3 border-2 border-primary border-t-transparent rounded-full"></div>
                        A carregar...
                      </>
                    ) : (
                      <>
                        <Upload className="h-3 w-3" />
                        Carregar Foto
                      </>
                    )}
                  </span>
                </label>
                {!formData.foto_url && (
                  <button
                    type="button"
                    onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                    className="px-3 py-1.5 text-xs font-medium rounded-full border bg-white hover:bg-stone-50 text-stone-600 border-stone-200"
                  >
                    {showAvatarPicker ? 'Fechar' : 'Escolher Avatar'}
                  </button>
                )}
              </div>
            </div>
            
            {/* Seletor de Avatar */}
            {showAvatarPicker && !formData.foto_url && (
              <div className="flex flex-wrap justify-center gap-2 p-3 bg-stone-50 rounded-lg">
                {AVATARS.map((av, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => { setFormData({ ...formData, avatar: av }); setShowAvatarPicker(false); }}
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-xl hover:bg-primary/10 transition-colors ${formData.avatar === av ? 'bg-primary/20 ring-2 ring-primary' : ''}`}
                  >
                    {av}
                  </button>
                ))}
              </div>
            )}

            {/* Nome e Apelido */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Nome *</label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({...formData, nome: e.target.value})}
                  placeholder="ex: Maria"
                  data-testid="nome-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Apelido *</label>
                <Input
                  value={formData.apelido}
                  onChange={(e) => setFormData({...formData, apelido: e.target.value})}
                  placeholder="ex: Silva"
                  data-testid="apelido-input"
                />
              </div>
            </div>

            {/* Data de Nascimento */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Data de Nascimento</label>
              <Input
                type="date"
                value={formData.data_nascimento}
                onChange={(e) => setFormData({...formData, data_nascimento: e.target.value})}
                data-testid="nascimento-input"
              />
            </div>

            {/* Tags */}
            {allTags.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2 flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Tags (opcional)
                </label>
                <TagSelector
                  selectedTags={formData.tags}
                  allTags={allTags}
                  onChange={(tags) => setFormData({ ...formData, tags })}
                />
              </div>
            )}

            {/* Vencimento Hora */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Vencimento por Hora (€) *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500">€</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.custo_hora}
                  onChange={(e) => setFormData({...formData, custo_hora: e.target.value})}
                  className="pl-8"
                  placeholder="10.00"
                  data-testid="custo-input"
                />
              </div>
            </div>

            {/* Username (auto-gerado ou editável) */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Username (login)</label>
              <Input
                value={formData.username || `${formData.nome.toLowerCase()}.${formData.apelido.toLowerCase()}`.replace(/\s+/g, '')}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                placeholder="Auto-gerado"
                className="font-mono"
                data-testid="username-input"
              />
              <p className="text-xs text-stone-400 mt-1">Usado para fazer login no sistema</p>
            </div>

            {/* Botões */}
            <div className="flex gap-3 pt-4">
              {editingOperador && (
                <Button
                  type="button"
                  variant="outline"
                  className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                  onClick={handleDeleteOperador}
                  disabled={loading}
                  data-testid="delete-operador-button"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Eliminar
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => { setShowDialog(false); resetForm(); }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-full"
                disabled={loading}
                data-testid="submit-operador-button"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'A guardar...' : 'Guardar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Gestão de Tags */}
      <GestaoTags
        open={showTagsDialog}
        onClose={() => setShowTagsDialog(false)}
        onTagsChange={loadTags}
      />

      {/* Modal Permissões de Operações */}
      {showPermissoes && (
        <OperadorPermissoes
          operadorId={showPermissoes.id}
          operadorNome={showPermissoes.nome_completo}
          onClose={() => setShowPermissoes(null)}
          onSave={() => {
            toast.success('Permissões guardadas com sucesso!');
            setShowPermissoes(null);
            loadOperadores(); // Recarregar lista para atualizar os cards
          }}
        />
      )}
    </GestorLayout>
  );
}
